import { Text } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import {
  FragmentType as ServerFragmentType,
  getFragmentData as getServerFragmentData,
  serverGql,
} from "__generated__/serverGql";
import { AccessPlanType } from "__generated__/gql/graphql";
import PlanCard, {
  PlanCardDisabledStyle,
} from "components/Core/AccountInfo/Billing/PlanSelection/PlanCard";
import Link from "components/UI/Links/Link";
import { CONTACT_PAGE } from "runtime-config/config";

const accessPlanFragment = gql(/* GraphQL */ `
  fragment SignupPlanCardAccessPlan on AccessPlan {
    id
    planType
    stripeProductId
    ...BillingPlanCardAccessPlan
  }
`);

const stripeProductFragment = serverGql(/* ServerGraphQL */ `
  fragment SignupPlanCardStripeProduct on StripeProduct {
    id
    ...BillingPlanCardStripeProduct
  }
`);

type Props = {
  accessPlan: FragmentType<typeof accessPlanFragment>;
  isCurrent: boolean;
  isPriceLoading: boolean;
  isSelected: boolean;
  isSignupPlanSelectionCompleted: boolean;
  onSelect: (accessPlanId: UUID) => void;
  stripeProductsById: Record<
    string,
    ServerFragmentType<typeof stripeProductFragment> | undefined
  >;
};

export default function SignupPlanCard({
  accessPlan: accessPlanFragmentRef,
  isCurrent,
  isPriceLoading,
  isSelected,
  isSignupPlanSelectionCompleted,
  onSelect,
  stripeProductsById,
}: Props) {
  const accessPlan = getFragmentData(accessPlanFragment, accessPlanFragmentRef);

  const isSignupSelectable =
    accessPlan.planType === AccessPlanType.FREE || !!accessPlan.stripeProductId;

  const stripeProduct = getServerFragmentData(
    stripeProductFragment,
    accessPlan.stripeProductId
      ? (stripeProductsById[accessPlan.stripeProductId] ?? null)
      : null,
  );

  return (
    <PlanCard
      accessPlan={accessPlan}
      disabledStyle={
        isSignupSelectable ? undefined : PlanCardDisabledStyle.UNAVAILABLE
      }
      isCurrent={isCurrent}
      isCurrentShown={isSignupPlanSelectionCompleted}
      isDisabled={
        (!isSignupPlanSelectionCompleted && !isSignupSelectable) ||
        (isSignupPlanSelectionCompleted && !isCurrent)
      }
      isSelected={
        !isSignupPlanSelectionCompleted && isSignupSelectable && isSelected
      }
      isPriceLoading={isPriceLoading}
      onSelect={
        !isSignupPlanSelectionCompleted && isSignupSelectable
          ? onSelect
          : undefined
      }
      stripeProduct={stripeProduct}
    >
      {!isSignupPlanSelectionCompleted && !isSignupSelectable && (
        <Text color="gray.600" fontSize="sm" mt={3}>
          For custom packages, our team will tailor the right plan with you.
          Choose a standard plan to continue and{" "}
          <Link
            color="magenta.dark"
            fontWeight="semibold"
            href={CONTACT_PAGE}
            isExternal
          >
            contact us
          </Link>
          .
        </Text>
      )}
    </PlanCard>
  );
}
